// web/src/lib/wordmarkFx.ts
// Tunable settings for the CreateScreen wordmark effects (roast heat-haze + flicker,
// toast sheen). MoodWordmark reads these via useWordmarkFx(); the DEV-only
// WordmarkFxDebug panel mutates them live. In production nothing mutates the store,
// so the shipped values are exactly WORDMARK_FX_DEFAULTS.
import { useSyncExternalStore } from "react";

export interface WordmarkFx {
  enabled: boolean;
  heatScale: number; // feDisplacementMap scale (heat-wave amount)
  heatFreqX: number; // feTurbulence baseFrequency X
  heatFreqY: number; // feTurbulence baseFrequency Y (vertical shimmer)
  heatDur: number; // seconds per upward scroll cycle (rise speed; lower = faster)
  heatTile: number; // noise tile size = wave scale + seamless scroll loop distance
  flicker: number; // brightness flicker amplitude (0 = off)
  sheenDur: number; // sheen sweep+pause cycle, seconds
  sheenOpacity: number; // sheen highlight opacity
  sheenWidth: number; // sheen band spread (gradient axis length)
  sheenAngle: number; // sheen band tilt, degrees (0 = vertical band)
  sheenStart: number; // mount phase 0-1: pre-rolls the sweep so it fires immediately on mount
  outline: boolean; // secondary sheen on an offset letter outline
  outlineWidth: number; // outline stroke width
  outlineOffset: number; // outline spatial offset (px)
  outlineDelay: number; // outline sheen timing vs the main sheen (fraction of cycle; 0 = same time, − leads, + trails)
}

export const WORDMARK_FX_DEFAULTS: WordmarkFx = {
  enabled: true,
  heatScale: 13,
  heatFreqX: 0.004,
  heatFreqY: 0.009,
  heatDur: 1,
  heatTile: 500,
  flicker: 0.05,
  sheenDur: 6,
  sheenOpacity: 0.6,
  sheenWidth: 5000,
  sheenAngle: 18,
  sheenStart: 0.35,
  outline: true,
  outlineWidth: 6,
  outlineOffset: 0,
  outlineDelay: -0.015,
};

let _fx: WordmarkFx = { ...WORDMARK_FX_DEFAULTS };
const _subs = new Set<() => void>();
function emit(): void {
  for (const cb of _subs) cb();
}

export const wordmarkFx = {
  get: (): WordmarkFx => _fx,
  set: (patch: Partial<WordmarkFx>): void => {
    _fx = { ..._fx, ...patch };
    emit();
  },
  reset: (): void => {
    _fx = { ...WORDMARK_FX_DEFAULTS };
    emit();
  },
  subscribe: (cb: () => void): (() => void) => {
    _subs.add(cb);
    return () => {
      _subs.delete(cb);
    };
  },
};

/** Reactive read of the current wordmark FX settings. */
export function useWordmarkFx(): WordmarkFx {
  return useSyncExternalStore(wordmarkFx.subscribe, wordmarkFx.get, () => WORDMARK_FX_DEFAULTS);
}
